import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, AlertTriangle, Lightbulb, Wrench, HelpCircle, FileText, Copy, Check, Loader2, Layers } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Verdict colors
const VERDICT_COLORS = {
  PASS: '#00ff88',
  FAIL: '#ff4466',
  CONDITIONAL: '#ffaa00'
};

// Risk level colors
const RISK_COLORS = {
  Low: '#00ff88',
  Medium: '#ffaa00',
  High: '#ff4466'
};

// Preset questions for quick analysis
// Preset button component
const PresetButton = ({ preset, onClick, disabled }) => (
  <button
    onClick={() => onClick(preset.query)}
    disabled={disabled}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    data-testid={`preset-${preset.id}`}
  >
    {preset.icon}
    {preset.label}
  </button>
);

// Preset questions data
const PRESET_QUESTIONS = [
  { 
    id: 'analyze',
    icon: <Sparkles className="w-3.5 h-3.5" />, 
    label: 'Analyze Assembly', 
    query: 'Analyze this mechanical assembly. Describe the main components you see, their arrangement, and how they work together.' 
  },
  { 
    id: 'failure',
    icon: <AlertTriangle className="w-3.5 h-3.5" />, 
    label: 'Failure Points', 
    query: 'Identify potential failure points in this assembly. What components are under the most stress? Where might fatigue or wear occur?' 
  },
  { 
    id: 'improve',
    icon: <Lightbulb className="w-3.5 h-3.5" />, 
    label: 'Design Improvements', 
    query: 'Suggest design improvements for this assembly. How could it be made more efficient, lighter, stronger, or easier to manufacture?' 
  },
  { 
    id: 'explain',
    icon: <HelpCircle className="w-3.5 h-3.5" />, 
    label: 'How It Works', 
    query: 'Explain how this mechanical system works. Describe the motion transfer, power flow, and operational principles.' 
  }
];

// Engineering Report Component - Helper to render risks
const RiskItem = ({ risk, index }) => (
  <div key={index} className="flex items-start gap-2">
    <div 
      className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
      style={{ backgroundColor: RISK_COLORS[risk.level] }}
    />
    <div>
      <span className="text-sm text-white font-medium">{risk.area}</span>
      <span className="text-xs text-gray-500 ml-2">({risk.level})</span>
      <p className="text-xs text-gray-400">{risk.detail}</p>
    </div>
  </div>
);

// Engineering Report Component - Helper to render recommendations
const RecommendationItem = ({ rec, index }) => (
  <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
    <span className="text-cyan-400 font-mono text-xs mt-0.5">{index + 1}.</span>
    {rec}
  </li>
);

// Engineering Report Component
const EngineeringReport = ({ report, onCopy }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    let risksText = '';
    for (let i = 0; i < report.risks.length; i++) {
      const r = report.risks[i];
      risksText += `- ${r.area} (${r.level}): ${r.detail}\n`;
    }
    
    let recsText = '';
    for (let i = 0; i < report.recommendations.length; i++) {
      recsText += `${i + 1}. ${report.recommendations[i]}\n`;
    }
    
    const text = `Engineering Inspection Report
Verdict: ${report.verdict}
Summary: ${report.summary}

Risks:
${risksText}
Recommendations:
${recsText}`;
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (onCopy) onCopy();
  };
  
  // Pre-render arrays
  const riskElements = [];
  for (let i = 0; i < report.risks.length; i++) {
    riskElements.push(<RiskItem key={i} risk={report.risks[i]} index={i} />);
  }
  
  const recElements = [];
  for (let i = 0; i < report.recommendations.length; i++) {
    recElements.push(<RecommendationItem key={i} rec={report.recommendations[i]} index={i} />);
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl p-4 bg-white/5 border border-white/10"
    >
      {/* Verdict Badge */}
      <div className="flex items-center justify-between mb-3">
        <div 
          className="px-3 py-1 rounded-full text-xs font-bold"
          style={{ 
            backgroundColor: `${VERDICT_COLORS[report.verdict]}20`,
            color: VERDICT_COLORS[report.verdict],
            border: `1px solid ${VERDICT_COLORS[report.verdict]}40`
          }}
        >
          {report.verdict}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied!' : 'Copy Report'}
        </button>
      </div>
      
      {/* Summary */}
      <p className="text-sm text-gray-300 mb-4 leading-relaxed">{report.summary}</p>
      
      {/* Risks */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-cyan-400 mb-2 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" />
          Risk Assessment
        </h4>
        <div className="space-y-2">
          {riskElements}
        </div>
      </div>
      
      {/* Recommendations */}
      <div>
        <h4 className="text-xs font-semibold text-cyan-400 mb-2 flex items-center gap-1">
          <Lightbulb className="w-3.5 h-3.5" />
          Recommendations
        </h4>
        <ol className="space-y-1.5">
          {recElements}
        </ol>
      </div>
    </motion.div>
  );
};

// Helper function to format bold text
const formatBoldText = (text, keyPrefix) => {
  if (!text) return text;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, j) => 
    j % 2 === 1 ? <strong key={`${keyPrefix}-${j}`} className="text-white">{part}</strong> : part
  );
};

// Simple markdown renderer
const renderMarkdown = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  
  const flushList = () => {
    if (listItems.length > 0) {
      const items = listItems.map((item, i) => <li key={i}>{item}</li>);
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-2 text-gray-300">
          {items}
        </ul>
      );
      listItems = [];
    }
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Headers
    if (line.startsWith('### ')) {
      flushList();
      elements.push(<h4 key={i} className="text-sm font-semibold text-cyan-400 mt-4 mb-2">{line.slice(4)}</h4>);
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(<h3 key={i} className="text-base font-semibold text-cyan-300 mt-4 mb-2">{line.slice(3)}</h3>);
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(<h2 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.slice(2)}</h2>);
    }
    // Bullet points
    else if (line.match(/^[-*]\s/)) {
      const content = line.slice(2);
      listItems.push(formatBoldText(content, `b-${i}`));
    }
    // Numbered lists
    else if (line.match(/^\d+\.\s/)) {
      const content = line.replace(/^\d+\.\s/, '');
      listItems.push(formatBoldText(content, `n-${i}`));
    }
    // Regular paragraphs
    else if (line.trim()) {
      flushList();
      elements.push(<p key={i} className="text-gray-300 my-2 leading-relaxed">{formatBoldText(line, `p-${i}`)}</p>);
    } else {
      flushList();
    }
  }
  
  flushList();
  return elements;
};

// Bouncing dots animation - single dot component
const BouncingDot = ({ index }) => (
  <motion.span
    className="w-1.5 h-1.5 bg-cyan-400 rounded-full"
    animate={{ y: [0, -4, 0] }}
    transition={{
      duration: 0.6,
      repeat: Infinity,
      delay: index * 0.15,
      ease: "easeInOut"
    }}
  />
);

// Bouncing dots animation
const BouncingDots = () => (
  <div className="flex items-center gap-1">
    <span className="text-gray-400">Analyzing assembly</span>
    <div className="flex gap-0.5 ml-1">
      <BouncingDot key={0} index={0} />
      <BouncingDot key={1} index={1} />
      <BouncingDot key={2} index={2} />
    </div>
  </div>
);

// Message bubble component
const MessageBubble = ({ msg, index }) => (
  <motion.div
    key={index}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={msg.role === 'user' ? 'flex justify-end' : ''}
  >
    {msg.role === 'user' ? (
      <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tr-sm bg-cyan-600 text-white text-sm">
        {msg.content}
      </div>
    ) : (
      <div className={`rounded-2xl rounded-tl-sm p-4 ${msg.error ? 'bg-red-500/10 border border-red-500/20' : 'bg-white/5'}`}>
        {msg.error ? (
          <p className="text-red-400 text-sm">{msg.content}</p>
        ) : (
          <div className="text-sm">
            {renderMarkdown(msg.content)}
          </div>
        )}
      </div>
    )}
  </motion.div>
);

const GeminiPanel = ({ isOpen, onClose, modelInfo, viewerRef, onExplodeChange }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [explodeSuggestion, setExplodeSuggestion] = useState(null);
  const [isGeneratingExplode, setIsGeneratingExplode] = useState(false);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, report]);
  
  // Capture canvas screenshot using viewer ref
  const captureCanvas = () => {
    // Use the viewer ref's captureScreenshot method which forces a render first
    if (viewerRef?.current?.captureScreenshot) {
      try {
        const dataUrl = viewerRef.current.captureScreenshot();
        if (dataUrl) {
          return dataUrl;
        }
      } catch (e) {
        console.error('Failed to capture via viewerRef:', e);
      }
    }
    
    // Fallback to direct canvas capture (may be black)
    const canvas = document.querySelector('canvas');
    if (!canvas) {
      console.error('Canvas not found');
      return null;
    }
    try {
      return canvas.toDataURL('image/jpeg', 0.85);
    } catch (e) {
      console.error('Failed to capture canvas:', e);
      return null;
    }
  };
  
  // Build context string from model info
  const buildContext = () => {
    if (!modelInfo) return '';
    const parts = [];
    if (modelInfo.name) parts.push(`Model: ${modelInfo.name}`);
    if (modelInfo.partCount) parts.push(`Parts: ${modelInfo.partCount}`);
    if (modelInfo.vertexCount) parts.push(`Vertices: ${modelInfo.vertexCount.toLocaleString()}`);
    if (modelInfo.explodeLevel !== undefined) parts.push(`Explode Level: ${Math.round(modelInfo.explodeLevel * 100)}%`);
    return parts.join(', ');
  };
  
  // Generate Engineering Report
  const handleGenerateReport = async () => {
    if (isGeneratingReport) return;
    
    setIsGeneratingReport(true);
    setReport(null);
    
    const imageData = captureCanvas();
    if (!imageData) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Unable to capture the 3D viewport.',
        error: true 
      }]);
      setIsGeneratingReport(false);
      return;
    }
    
    const prompt = `Generate an engineering inspection report for this mechanical assembly. Respond ONLY in this exact JSON format: { "verdict": "PASS or FAIL or CONDITIONAL", "summary": "2 sentence summary", "risks": [{ "area": "name", "level": "Low or Medium or High", "detail": "1 sentence" }], "recommendations": ["recommendation 1", "recommendation 2", "recommendation 3"] }`;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          question: prompt,
          context: buildContext()
        })
      });
      
      if (!response.ok) throw new Error('API request failed');
      
      const data = await response.json();
      
      // Try to parse JSON from response
      try {
        let jsonStr = data.response;
        const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonStr = jsonMatch[0];
        }
        const parsed = JSON.parse(jsonStr);
        if (parsed.verdict && parsed.summary) {
          setReport(parsed);
        } else {
          throw new Error('Invalid format');
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        // Fallback report
        setReport({
          verdict: 'CONDITIONAL',
          summary: data.response.substring(0, 200),
          risks: [{ area: 'Analysis', level: 'Medium', detail: 'See full response below' }],
          recommendations: ['Review full AI analysis', 'Consult engineering team', 'Perform detailed inspection']
        });
      }
    } catch (err) {
      console.error('Report generation failed:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Report generation failed: ${err.message}`,
        error: true 
      }]);
    } finally {
      setIsGeneratingReport(false);
    }
  };
  
  // AI Smart Explode
  const handleAIExplode = async () => {
    if (isGeneratingExplode) return;
    
    setIsGeneratingExplode(true);
    setExplodeSuggestion(null);
    
    const imageData = captureCanvas();
    if (!imageData) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Unable to capture the 3D viewport.',
        error: true 
      }]);
      setIsGeneratingExplode(false);
      return;
    }
    
    const prompt = `You are looking at a 3D mechanical assembly. Suggest the ideal explode level for inspection. The explode level ranges from 0 to 50 percent (NOT higher). Respond ONLY in JSON: { "suggestion": "1 sentence about what exploding reveals", "recommended_level": 35, "sequence_description": "describe the order parts should separate for best understanding" }`;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          question: prompt,
          context: buildContext()
        })
      });
      
      if (!response.ok) throw new Error('API request failed');
      
      const data = await response.json();
      
      // Try to parse JSON from response
      try {
        let jsonStr = data.response;
        const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonStr = jsonMatch[0];
        }
        const parsed = JSON.parse(jsonStr);
        if (parsed.suggestion && parsed.recommended_level !== undefined) {
          setExplodeSuggestion(parsed);
        } else {
          throw new Error('Invalid format');
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        // Fallback
        setExplodeSuggestion({
          suggestion: 'Explode to reveal internal components and connection points.',
          recommended_level: 35,
          sequence_description: 'Parts will separate from center outward, revealing the assembly structure.'
        });
      }
    } catch (err) {
      console.error('AI Explode failed:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `AI Explode analysis failed: ${err.message}`,
        error: true 
      }]);
    } finally {
      setIsGeneratingExplode(false);
    }
  };
  
  // Apply explode suggestion
  const applyExplodeSuggestion = () => {
    if (!explodeSuggestion || !onExplodeChange) return;
    // Cap the explode level to 50% max to keep parts visible on screen
    const rawLevel = explodeSuggestion.recommended_level / 100;
    const level = Math.min(rawLevel, 0.5); // Max 50% to keep parts in view
    onExplodeChange(level, true, 1500); // animated
    setExplodeSuggestion(null);
  };
  
  const handleSubmit = async (query = input) => {
    if (!query.trim() || isLoading) return;
    
    // Capture the canvas
    const imageData = captureCanvas();
    if (!imageData) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Unable to capture the 3D viewport. Please ensure the model is loaded.',
        error: true 
      }]);
      return;
    }
    
    // Add user message
    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          question: query,
          context: buildContext()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'AI analysis failed');
      }
      
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('AI query failed:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Analysis failed: ${error.message}. Please try again.`,
        error: true 
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePresetClick = (query) => {
    handleSubmit(query);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed top-0 right-0 h-full w-[380px] z-50 flex flex-col"
          style={{
            background: 'rgba(10, 10, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          initial={{ x: 380, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 380, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          data-testid="gemini-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                {/* Green status dot */}
                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#0a0a19]" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                  <span className="text-cyan-400">✦</span> Gemini 3 Engineering AI
                </h3>
                <p className="text-[10px] text-gray-500">Multimodal Analysis</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="gemini-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Preset Questions */}
          <div className="px-4 py-3 border-b border-white/5">
            <div className="flex flex-wrap gap-2">
              <PresetButton key="analyze" preset={PRESET_QUESTIONS[0]} onClick={handlePresetClick} disabled={isLoading || isGeneratingReport || isGeneratingExplode} />
              <PresetButton key="failure" preset={PRESET_QUESTIONS[1]} onClick={handlePresetClick} disabled={isLoading || isGeneratingReport || isGeneratingExplode} />
              <PresetButton key="improve" preset={PRESET_QUESTIONS[2]} onClick={handlePresetClick} disabled={isLoading || isGeneratingReport || isGeneratingExplode} />
              <PresetButton key="explain" preset={PRESET_QUESTIONS[3]} onClick={handlePresetClick} disabled={isLoading || isGeneratingReport || isGeneratingExplode} />
              
              {/* AI Explode Chip */}
              <button
                onClick={handleAIExplode}
                disabled={isLoading || isGeneratingReport || isGeneratingExplode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-400/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="preset-ai-explode"
              >
                <Layers className="w-3.5 h-3.5" />
                {isGeneratingExplode ? 'Analyzing...' : '✦ AI Explode'}
              </button>
            </div>
            
            {/* AI Explode Suggestion Card */}
            {explodeSuggestion && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20"
              >
                <div className="flex items-start gap-2 mb-2">
                  <Layers className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-medium mb-1">{explodeSuggestion.suggestion}</p>
                    <p className="text-xs text-gray-400">{explodeSuggestion.sequence_description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-purple-300">
                    Recommended: <span className="font-bold">{explodeSuggestion.recommended_level}%</span>
                  </span>
                  <button
                    onClick={applyExplodeSuggestion}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium transition-colors shadow-lg shadow-purple-500/20"
                    data-testid="apply-explode-btn"
                  >
                    Apply <span className="text-purple-200">✦</span>
                  </button>
                </div>
              </motion.div>
            )}
            
            {/* Generate Report Button */}
            <button
              onClick={handleGenerateReport}
              disabled={isLoading || isGeneratingReport || isGeneratingExplode}
              className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/20"
              data-testid="generate-report-btn"
            >
              {isGeneratingReport ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  <span className="text-cyan-200">✦</span> Generate Report
                </>
              )}
            </button>
          </div>
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {/* Engineering Report */}
            {report && (
              <EngineeringReport report={report} onCopy={() => {}} />
            )}
            
            {messages.length === 0 && !isLoading && !report && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-cyan-400" />
                </div>
                <h4 className="text-white font-medium mb-2">Ready to Analyze</h4>
                <p className="text-gray-500 text-sm max-w-[280px] mx-auto">
                  Click a preset question or generate a report to analyze the current 3D viewport with Gemini 3.
                </p>
              </div>
            )}
            
            {messages.length > 0 && messages.reduce((acc, msg, i) => {
              acc.push(<MessageBubble key={i} msg={msg} index={i} />);
              return acc;
            }, [])}
            
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white/5 rounded-2xl rounded-tl-sm p-4"
              >
                <BouncingDots />
              </motion.div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input Area */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about this assembly..."
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/25 disabled:opacity-50 transition-all"
                data-testid="gemini-input"
              />
              <button
                onClick={() => handleSubmit()}
                disabled={!input.trim() || isLoading}
                className="h-10 w-10 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-cyan-600/50 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-lg shadow-cyan-500/20"
                data-testid="gemini-send-btn"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-[10px] text-gray-600 mt-2 text-center">
              Powered by Gemini 3 • Captures current viewport for analysis
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GeminiPanel;
